import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { displayZone, type TimeZoneMode } from "@/shared/components/Timeline";
import { useDateTimeFormat } from "@/shared/datetime";
import type { SessionRecord } from "@/shared/domain/sessions/SessionRecord";
import { Collapsible, Table } from "@/shared/foundation/primitives";
import { cellStyles } from "@/shared/foundation/primitives/Table";
import { Goto } from "@/shared/navigation";

import { UNTITLED_SESSION } from "./sessionTracks";
import styles from "./UntimedSessionsTray.module.css";

const PLACEHOLDER = "—";

export interface UntimedSessionsTrayProps {
  sessions: readonly SessionRecord[];
  /** The clock the last-modified dates are read on, the timeline's Local/UTC choice. */
  tz: TimeZoneMode;
  /** Whether the tray opens expanded. @default false */
  defaultOpen?: boolean;
}

/**
 * Sessions with no time range are counted and reachable, never dropped: a
 * collapsible card under the timeline with a table of them. Each name opens
 * its session's page in a new tab, where the user can add the files that give
 * it a time range without losing their place on the timeline.
 */
export function UntimedSessionsTray({
  sessions,
  tz,
  defaultOpen = false,
}: UntimedSessionsTrayProps) {
  const format = useDateTimeFormat(displayZone(tz));
  const count = sessions.length;
  return (
    <Collapsible.Root defaultOpen={defaultOpen}>
      <h3 className={styles.heading}>
        <Collapsible.Trigger className={styles.trigger}>
          <span>
            {count} {count === 1 ? "session has" : "sessions have"} no time
            range yet
          </span>
        </Collapsible.Trigger>
      </h3>
      <Collapsible.Panel className={styles.panel}>
        <Table className={styles.table}>
          <Table.Header variant="compact">
            <Table.Row>
              <Table.Head>Name</Table.Head>
              <Table.Head>ID</Table.Head>
              <Table.Head>Last modified</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sessions.map((session) => (
              <Table.Row key={session.session_id}>
                <Table.Cell>
                  <RobotoLinkHoverUnderline
                    className={cellStyles.primaryContent}
                    to={Goto.createSessionRoute(session.session_id)}
                    // A new tab, so adding files to the session does not
                    // lose the user's place on the timeline. noopener denies
                    // the tab access to window.opener; noreferrer also
                    // withholds the Referer header.
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {session.name ?? UNTITLED_SESSION}
                  </RobotoLinkHoverUnderline>
                </Table.Cell>
                <Table.Cell className={cellStyles.idCell}>
                  <code>{session.session_id}</code>
                </Table.Cell>
                <Table.Cell className={styles.modified}>
                  {session.modified
                    ? format.format(new Date(session.modified))
                    : PLACEHOLDER}{" "}
                  by{" "}
                  <RobotoPrincipalUnderlineLink
                    className={styles.modifiedBy}
                    principal={session.modified_by}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}
